import { sortByName, toGroupValue } from "modules/BrandsDashboard/utils";
import { SELECT_ALL_DROPDOWN_VALUE } from "utils/common";

import type { DropdownItemProps } from "components";
import type { Brand, BrandGroup, BrandGroupID, BrandID } from "models";

export type GroupedBrands = {
  brandGroups: BrandGroup[];
  brandsByGroupId: Map<BrandGroupID, Brand[]>;
};

type GetGroupedBrandOptionsProps = {
  groupedBrands: GroupedBrands;
  selectedOptionValues: string[];
  allBrandsText?: string;
};

type ToBrandGroupIdsProps = {
  brandGroups: BrandGroup[];
  selectedValues: string[];
  selectedBrandGroupIds: BrandGroupID[];
};

const BRAND_VALUE_PREFIX = "brand-";

export const ALL_BRANDS_VALUE = `${SELECT_ALL_DROPDOWN_VALUE}`;

const toBrandValue = (brandId: BrandID) => `${BRAND_VALUE_PREFIX}${brandId}`;

const getBrandOptions = (brands: Brand[]): DropdownItemProps<string>[] =>
  brands.map(brand => ({ key: toBrandValue(brand.id), value: toBrandValue(brand.id), text: brand.name, className: "brand-group-item", disabled: true }));

export const toGroupedBrands = (activeBrands: Brand[], brandGroups: BrandGroup[]): GroupedBrands => {
  const brandsByGroupId = new Map<BrandGroupID, Brand[]>();

  activeBrands.forEach(brand => {
    if (!brand.brand_group_id) return;

    const groupBrands = brandsByGroupId.get(brand.brand_group_id);

    if (groupBrands) groupBrands.push(brand);
    else brandsByGroupId.set(brand.brand_group_id, [brand]);
  });

  brandsByGroupId.forEach(groupBrands => groupBrands.sort(sortByName));

  return { brandGroups: brandGroups.filter(brandGroup => brandsByGroupId.has(brandGroup.id)).sort(sortByName), brandsByGroupId };
};

export const getGroupedBrandOptions = ({ groupedBrands, selectedOptionValues, allBrandsText }: GetGroupedBrandOptionsProps): DropdownItemProps<string>[] => {
  const { brandGroups, brandsByGroupId } = groupedBrands;

  const isEveryGroupSelected = brandGroups.every(brandGroup => selectedOptionValues.includes(toGroupValue(brandGroup.id)));
  const allBrandsOption: DropdownItemProps<string>[] =
    allBrandsText && !isEveryGroupSelected ? [{ key: ALL_BRANDS_VALUE, value: ALL_BRANDS_VALUE, text: allBrandsText }] : [];

  const groupOptions = brandGroups.flatMap<DropdownItemProps<string>>(brandGroup => {
    const groupValue = toGroupValue(brandGroup.id);

    return [
      { key: groupValue, value: groupValue, text: brandGroup.name, icon: "circle g", className: "brand-group-header" },
      ...(selectedOptionValues.includes(groupValue) ? [] : getBrandOptions(brandsByGroupId.get(brandGroup.id) ?? []))
    ];
  });

  return [...allBrandsOption, ...groupOptions];
};

export const toBrandGroupIds = ({ brandGroups, selectedValues, selectedBrandGroupIds }: ToBrandGroupIdsProps) => {
  const brandGroupIds = brandGroups.map(brandGroup => brandGroup.id);
  if (selectedValues.includes(ALL_BRANDS_VALUE)) return brandGroupIds;

  const brandGroupIdByValue = new Map([...selectedBrandGroupIds, ...brandGroupIds].map(brandGroupId => [toGroupValue(brandGroupId), brandGroupId]));

  return selectedValues.flatMap(selectedValue => brandGroupIdByValue.get(selectedValue) ?? []);
};
